import { some } from '@utils/some';
import { UseFormReturn } from 'react-hook-form';
import { Price } from 'shared-types';
import { usePackageConfiguration, useWizardNavigation, CurrentStepProvider } from '../contexts';
import { DynamicPackagesFormStepNames, PackagesFormValues } from '../types';
import { ExperiencesStep } from './ExperiencesStep';
import { ExperiencesStepV2 } from './ExperiencesStepV2';
import { GuestDetailsStep } from './GuestDetailsStep';
import { GuestDetailsStepV2 } from './GuestDetailsStepV2';
import { SelectExperienceStep } from './SelectExperienceStep';
import { TablesStep } from './TablesStep';
import { TablesStepV2 } from './TablesStepV2';
import { MultiRoomGuestDetailsStep } from './MultiRoomGuestDetailsStep';
import { RoomExtrasStep } from './RoomExtrasStep';
import { SpaExtrasStep } from './SpaExtrasStep';

type FormStepsProps = {
  form: UseFormReturn<PackagesFormValues>;
  isSubmitting: boolean;
  addToBasket: () => void;
};

export const useGetPackageTotalPrice = (
  roomPrice: number,
  currencyCode: string,
  form: UseFormReturn<PackagesFormValues>
) => {
  const roomExtrasTotal =
    form.watch('extras')?.reduce((acc, e) => acc + (e.isSelected ? e.price?.amount || 0 : 0), 0) ||
    0;

  const spaExtrasTotal =
    form
      .watch('spaExtras')
      ?.reduce((acc, e) => acc + (some(e.selectedGuestDetails) ? e.priceAmount : 0), 0) || 0;

  const packageTotal = roomPrice + roomExtrasTotal + spaExtrasTotal;

  const packagePrice: Price = {
    amount: packageTotal,
    currencyCode: currencyCode,
  };

  return packagePrice;
};

export const FormSteps = ({ form, isSubmitting, addToBasket }: FormStepsProps) => {
  const { property, rate, room, journeyParams, isEcom2Enabled } = usePackageConfiguration();

  const { currentFormStep } = useWizardNavigation();

  const { rooms } = rate;
  const selectedRoom = rooms?.find(({ id }) => String(room.id) === String(id));
  const isMultiRoom = some(journeyParams?.rooms || []) && (journeyParams?.rooms || []).length > 1;

  const packagePrice = useGetPackageTotalPrice(
    selectedRoom?.pricePerStay.amount || 0,
    property.defaultCurrency,
    form
  );

  const currentStepType = currentFormStep.step;

  const currentStepValue = {
    packageTotalPrice: packagePrice,
    isSubmitting,
    addToBasket,
    hash: currentStepType,
  };

  const guestsStep = isMultiRoom ? (
    <MultiRoomGuestDetailsStep />
  ) : isEcom2Enabled ? (
    <GuestDetailsStepV2 />
  ) : (
    <GuestDetailsStep />
  );

  const formStep = {
    [DynamicPackagesFormStepNames.GUESTS]: guestsStep,
    [DynamicPackagesFormStepNames.ROOM_EXTRAS]: <RoomExtrasStep />,
    [DynamicPackagesFormStepNames.SELECT_SPA_EXPERIENCE]: <SelectExperienceStep />,
    [DynamicPackagesFormStepNames.EXPERIENCE]: isEcom2Enabled ? (
      <ExperiencesStepV2 />
    ) : (
      <ExperiencesStep />
    ),
    [DynamicPackagesFormStepNames.SPA_EXTRAS]: <SpaExtrasStep />,
    [DynamicPackagesFormStepNames.TABLES]: isEcom2Enabled ? <TablesStepV2 /> : <TablesStep />,
  };

  return (
    <CurrentStepProvider value={currentStepValue}>
      {formStep?.[currentStepType] || <></>}
    </CurrentStepProvider>
  );
};
